import type { StripeCardElement } from '@stripe/stripe-js'
import type { ComponentProps } from 'react'
import { useForm } from 'react-hook-form'
import {
	getGetAccountPaymentMethodsQueryKey,
	useGetAccountPaymentMethods,
} from '@cocoonspace/sdk-js/domains/account/account'
import { onNestedSubmit } from '@cocoonspace/shared/utils/on-nested-submit'
import { Button } from '@cocoonspace/ui/components/button'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '@cocoonspace/ui/components/card'
import { Form } from '@cocoonspace/ui/components/form'
import { FormControlledInput } from '@cocoonspace/ui/components/form-controlled-input'
import { Input } from '@cocoonspace/ui/components/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLingui } from '@lingui/react/macro'
import { CardElement, useElements } from '@stripe/react-stripe-js'
import { useQueryClient } from '@tanstack/react-query'
import { usePathname } from 'next/navigation'
import { toast } from 'sonner'
import * as z from 'zod'

import { StripeCardInput } from '~/domains/payments/components/atoms/stripe-card-input'
import { stripLocaleFromPath } from '~/i18n/routing'
import { useTracking } from '~/providers/tracking-provider'
import { useCreatePaymentMethod } from '../../hooks/use-create-payment-method.hook'

export interface FormValues {
	title?: string
	name: string
	card: StripeCardElement
}

export type PmFormValues = z.infer<typeof schema>

const schema = z.object({
	card: z.any(),
	name: z.string().min(1),
	title: z.string().optional(),
})

export const FormAddNewCard = ({
	onSubmitSuccess,
	addFormButtonVariant = 'default',
}: {
	addFormButtonVariant?: ComponentProps<typeof Button>['variant']
	onSubmitSuccess?: VoidFunction
}) => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const { t } = useLingui()
	const queryClient = useQueryClient()
	const { track } = useTracking()
	const pmCreateMutation = useCreatePaymentMethod()
	const elements = useElements()

	const { data: paymentMethods } = useGetAccountPaymentMethods({
		query: {
			select: (res) => res.data.filter((pm) => pm.type === 'card') || [],
		},
	})

	const form = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			card: undefined,
			name: '',
			title: '',
		},
	})

	const onSubmit = ({ title, name, card }: FormValues) =>
		pmCreateMutation
			.mutateAsync({
				card,
				name,
				title: title || '',
				type: 'card',
			})
			.then((res) => {
				if (res?.error) {
					toast.error(res.error.error.message!)
				} else {
					toast.success(t`Votre carte a été ajoutée`)

					queryClient.invalidateQueries({
						queryKey: getGetAccountPaymentMethodsQueryKey(),
					})

					onSubmitSuccess?.()

					form.reset()
					elements?.getElement(CardElement)?.clear()

					track.paymentMethodUpdate({ action: 'add', route: pathname })
				}
			})
			.catch(() => {
				toast.error(t`Une erreur est survenue`)
			})

	return (
		<Card variant='outline'>
			<CardHeader>
				<CardTitle>
					{t`Ajouter une carte de paiement`}
					<span className='ml-2 pb-[2px] text-xs font-normal'>(max 3)</span>
				</CardTitle>
			</CardHeader>

			<CardContent>
				<Form {...form}>
					<form
						onSubmit={onNestedSubmit({
							handleSubmit: form.handleSubmit,
							submitFunction: onSubmit,
						})}
					>
						<FormControlledInput
							control={form.control}
							label={t`Nom de la carte`}
							name='title'
							render={({ field }) => <Input {...field} />}
						/>

						<FormControlledInput
							control={form.control}
							label={t`Prénom et nom du titulaire`}
							name='name'
							render={({ field }) => <Input {...field} />}
							required
						/>

						<FormControlledInput
							control={form.control}
							label={t`Numéro de carte bancaire`}
							name='card'
							render={({ field }) => (
								<StripeCardInput
									onChange={field.onChange}
									onError={(message) =>
										message
											? form.setError('card', { message })
											: form.clearErrors('card')
									}
								/>
							)}
							required
						/>

						<Button
							className='w-full'
							data-testid='add-payment-card-button'
							disabled={
								!!(paymentMethods?.length && paymentMethods?.length >= 3)
							}
							type='submit'
							variant={addFormButtonVariant}
						>
							{t`Ajouter`}
						</Button>
					</form>
				</Form>
			</CardContent>
		</Card>
	)
}
